import clsx from 'clsx';
import React from 'react';
import { Routes } from 'constants/routes';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';
import { syncShallowSearchUrl } from 'utils/search-query';
import styles from './styles.module.css';

import { Icon } from 'common/icon';
import Link from 'next/link';
import { useMemo } from 'react';
import { allFAQ } from 'constants/faq';
import { SearchLink } from 'components/search-link';
import { ResultSearch } from 'components/header/result-search';
import { SITE_URL } from 'api/constants';

export const Search = ({
  variant,
  placeholder = 'Check out the RTP online casino.',
  anchor,
  rounded,
  onSearch,
}: {
  variant: string;
  placeholder?: string;
  anchor?: string;
  rounded?: boolean;
  onSearch?: (value: string) => void;
}) => {
  const router = useRouter();
  const search = router.query.q2
    ? String(decodeURIComponent(router.query.q2?.toString()))
    : '';
  const searchRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState('');
  const debounceValue = useDebounce(filter, 500);

  useEffect(() => {
    if (search) {
      setFilter(search);
      searchRef.current?.focus();
    }
  }, [search]);
  useEffect(() => {
    if (router.pathname === Routes.home) {
      syncShallowSearchUrl(router, Routes.home, debounceValue, 'q2');
      return;
    }

    onSearch?.(debounceValue);
  }, [debounceValue, onSearch, router]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (variant === 'slots') {
        return;
      }

      const searchUrl = `${Routes.search}?q2=${encodeURIComponent(filter)}`;
      router.push(searchUrl);
    }
  };

  const dynamicStyle = useMemo(() => {
    if (variant === 'home') {
      return {
        classContainer: styles.container_home,
      };
    }
    if (variant === 'slots') {
      return {
        classContainer: styles.container_slots,
      };
    }
    if (variant === 'allcasinos') {
      return {
        classContainer: styles.container_allcasinos,
      };
    }
    return {
      classContainer: null,
    };
  }, [variant]);

  const content = useMemo(() => {
    switch (variant) {
      case 'home':
        return null;
      case 'slots':
        return null;
      case 'streamer':
        return null;
      case 'allcasinos':
        return null;
      default:
        return <span className={styles.default_text}>{'Check Casino'}</span>;
    }
  }, [variant]);

  return (
    <div
      className={clsx(styles.container, dynamicStyle.classContainer, {
        [styles.rounded]: rounded,
      })}
      id={anchor}
    >
      <div
        className={clsx(
          styles.content,
          variant === 'streamer' && styles.no_padding
        )}
      >
        {content}

        <div ref={containerRef}>
          <div className={styles.checkContainer}>
            <Link
              aria-label="Search casinos or slots or streamers"
              prefetch={false}
              href={Routes.search}
              className={styles.icon}
            >
              <Icon.Search color={'black'} width="18px" height="18px" />
            </Link>
            <input
              type="text"
              value={filter}
              ref={searchRef}
              className={styles.searchInput}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholder ?? ''}
            />
            {(filter.length || search?.length) && (
              <button
                onClick={() => {
                  setFilter('');
                  searchRef.current?.focus();
                }}
                className={styles.clearContainer}
              >
                <Icon.Close height={20} width={20} />
                <span>{'Clear'}</span>
              </button>
            )}
          </div>
          {variant != 'allcasinos' && (
            <div className={styles.result_search__container}>
              <ResultSearch
                variant={variant}
                searchRef={searchRef}
                searchText={debounceValue}
                containerRef={containerRef}
              />
            </div>
          )}
        </div>
        {variant === 'home' &&
          [
            {
              title: `What is RTP and why is it important in online casinos?`,
              href:
                SITE_URL +
                Routes.guidePage(
                  'what-is-rtp-in-casino-games-and-why-is-it-important'
                ),
            },
            {
              title: `How can I find out the RTP of a casino game?`,
              href:
                SITE_URL +
                Routes.guidePage(
                  'how-to-find-the-rtp-for-big-time-gaming-slots'
                ),
            },
          ].map((item, index) => (
            <div className={styles.searchLinkContainer} key={index}>
              <SearchLink
                href={item.href}
                title={item.title}
                variant="small"
                className={
                  index === allFAQ.homeSearch.length - 1
                    ? styles.lastItem
                    : undefined
                }
              />

              {index === 0 && (
                <div
                  style={{
                    height: '1px',
                    width: '100%',
                    backgroundColor: '#121212',
                  }}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};
